/* eslint-disable dot-notation */
import { GLOBAL } from 'saddlebag-browser';
import observer from 'saddlebag-observer';

import { isKetchMode, isPurposeScopedIdServiceEnabled } from './cmp-state';
import {
  getSingleValueCookie,
  getMultiValueCookie,
  cookieExists,
} from './cookie-factory';
import {
  getEffectiveLatestVersion,
  maybeDowngradeCookieVersion,
} from './cookie-version-utils';
import { checkImportEnvironment } from './import-check';
import {
  attachGdprActiveField,
  getPreferencesFromCookie,
  getVersion,
  isRegulatedMarket,
  REGULATED_MARKET_FALLBACK_OPTIN_VALUE,
  PREFERENCES,
  GDPR_COOKIE_NAME,
  GDPR_COOKIE_MAX_AGE_IN_DAYS,
  LATEST_VERSION,
  NON_REGULATED_MARKET_DEFAULTS,
  REGULATED_MARKET_DEFAULTS,
  IS_GDPR_ACTIVE_FIELD_NAME,
  TC_STRING_DEFAULT_VALUE,
  type MarketDefaults,
  type TrackingPreferences,
  type PreferenceName,
} from './isomorphic';
import {
  getPreferencesFromKetch,
  setPreferencesViaKetch,
  showBannerViaKetch,
  isGdprInitialisedViaKetch,
  isGdprInitSessionActiveViaKetch,
} from './ketch-mapping';
import { refreshPurposeScopedId } from './purpose-scoped-id-service';

import type { MultiValueCookieInstance } from './cookie-factory';

const firePreferencesChangedEvent = (): void => {
  observer.publish('user-preferences-changed'); // TODO, 29/10/2018 - this event is deprecated and should be removed as soon as possible
  observer.publish('tracking-preferences-changed');
};

const GDPR_SESSION_COOKIE_FLAG = 'init' as const;

const toBoolean = (str: string | undefined | null) => (str || '') === 'true';

const preferenceNames = new Set<PreferenceName>(
  Object.values(PREFERENCES) as PreferenceName[],
);

const isKnownKey = (keyName: string): keyName is PreferenceName =>
  keyName === IS_GDPR_ACTIVE_FIELD_NAME ||
  preferenceNames.has(keyName as PreferenceName);

const isLatestCookieVersion = (): boolean => {
  // Don't use getPreferences here!
  // If the version is not in the cookie getPreferences will fall back to the latest version, rather than undefined.
  // Use >= to accept both v3 (existing users) and v4 (new users with purpose-scoped ID service enabled).
  // This ensures existing v3 users don't see the banner when the experiment is enabled.
  const cookie = getMultiValueCookie(GDPR_COOKIE_NAME);
  return getVersion(cookie) >= LATEST_VERSION;
};

const resetInitialLoadFlag = () => {
  const $window = GLOBAL.getWindow() as Window;
  if (
    $window.Sb &&
    $window.Sb['trackingPreferences'] &&
    // @ts-expect-error -- window based storage is not typed
    $window.Sb['trackingPreferences'].initialLoad
  ) {
    // @ts-expect-error -- window based storage is not typed
    delete $window.Sb['trackingPreferences'].initialLoad;
  }
};

const ensureNoLegacyGdprCookie = () => {
  const gdprSingleCookie = getSingleValueCookie(GDPR_COOKIE_NAME);
  if ((gdprSingleCookie.getValue() || '').indexOf('version:::') === -1) {
    gdprSingleCookie.remove();
  }
};

const storeGdprCookie = (
  gdprCookie: MultiValueCookieInstance,
  initialLoad: boolean,
): void => {
  if (initialLoad) {
    gdprCookie.setValue(GDPR_SESSION_COOKIE_FLAG, 'true');
    gdprCookie.store(null, { secure: false }); // Save it as a session cookie first
  } else {
    gdprCookie.deleteValue(GDPR_SESSION_COOKIE_FLAG);
    gdprCookie.store(GDPR_COOKIE_MAX_AGE_IN_DAYS, { secure: false });
    resetInitialLoadFlag();
  }
};

/**
 * Store cookie as a permanent cookie if it was a session cookie.
 * @param {MultiValueCookieInstance} gdprCookie GDPR cookie
 */
const convertSessionCookieToNormal = (
  gdprCookie: MultiValueCookieInstance,
): void => {
  const isSessionCookie = toBoolean(
    gdprCookie.getValue(GDPR_SESSION_COOKIE_FLAG),
  );
  if (isSessionCookie) {
    storeGdprCookie(gdprCookie, false);
  }
};

/**
 * Returns the default preferences for the selected marked to be set to the GDPR cookie during initialisation.
 * The market is necessary because the default preferences may vary between the markets.
 * @param market a market string, for example ('BG', 'US', 'RU'...)
 * @returns the default preferences for the market
 */
const getDefaultInitialisationPreferences = (market: string): MarketDefaults =>
  isRegulatedMarket(market)
    ? REGULATED_MARKET_DEFAULTS
    : NON_REGULATED_MARKET_DEFAULTS;

/**
 * Sets the tracking preferences for the market
 * @param prefs the preferences to be set or null for setting the default preferences for the market
 * @param initialLoad set to true if this set call happens on initial load without any GDPR cookie present, defaults to false
 */
const setPreferencesToCookie = (
  prefs: Partial<TrackingPreferences> = {},
  initialLoad = false,
): void => {
  // Route to Ketch when enabled (ignore initialLoad in Ketch mode)
  if (isKetchMode()) {
    setPreferencesViaKetch(prefs);
    return;
  }

  if (prefs) {
    Object.keys(prefs).forEach((key) => {
      if (!isKnownKey(key)) {
        throw new Error('Invalid preference name provided!');
      }
    });
  }
  ensureNoLegacyGdprCookie();

  const gdprCookie = getMultiValueCookie(GDPR_COOKIE_NAME);

  const preferences = {
    ...prefs,
    [PREFERENCES.VERSION]: getEffectiveLatestVersion(),
  };

  // TODO 15/10/2018: Enable the following code block when consumers of optin have confirmed they have updated to consume new keys instead
  // Remove legacy 'optin' key
  // gdprCookie.deleteValue('optin');

  Object.keys(preferences).forEach((name) => {
    // Ignoring 'optin' as it's a legacy key
    const value = preferences[name as Exclude<PreferenceName, 'optin'>];
    gdprCookie.setValue(name, value);
  });

  gdprCookie
    .keys()
    .filter((key) => !preferenceNames.has(key as PreferenceName))
    .forEach((key) => gdprCookie.deleteValue(key));

  storeGdprCookie(gdprCookie, initialLoad);
  firePreferencesChangedEvent();

  // Call purpose-scoped ID service to sync tracking ID with consent state
  // Fire and forget - errors are logged internally but don't block the flow
  // Only call when not initial load (user has made an explicit consent choice)
  if (!initialLoad && isPurposeScopedIdServiceEnabled()) {
    // Build preferences from the prefs object we just set (not from reading cookie)
    // This ensures we use the actual values that were passed in
    const effectivePreferences: TrackingPreferences = {
      [PREFERENCES.INFORMATION]: prefs?.[PREFERENCES.INFORMATION] === true,
      [PREFERENCES.ADVERTS]: prefs?.[PREFERENCES.ADVERTS] === true,
      [PREFERENCES.TC_STRING]:
        prefs?.[PREFERENCES.TC_STRING] || TC_STRING_DEFAULT_VALUE,
      [PREFERENCES.VERSION]: getEffectiveLatestVersion(),
      [IS_GDPR_ACTIVE_FIELD_NAME]: true,
    };
    refreshPurposeScopedId(effectivePreferences);
  }
};

/**
 * Sets the tracking preferences for the market
 * @param prefs the preferences to be set or null for setting the default preferences for the market
 * @returns The function does not return a result
 */
const setPreferences = (prefs?: Partial<TrackingPreferences>): void =>
  setPreferencesToCookie(prefs, false);

/**
 * Checks if the GDPR cookie is present and valid according to the latest posible desired version.
 * @returns true in case the cookie is present and valid and false otherwise.
 */
const isGdprInitialised = (): boolean => {
  // Route to Ketch when enabled
  if (isKetchMode()) {
    return isGdprInitialisedViaKetch();
  }

  return cookieExists(GDPR_COOKIE_NAME) && isLatestCookieVersion();
};

/**
 * Checks if the GDPR cookie is present and has the session cookie flag on it .
 * @returns true in case and has the session cookie flag on it, false otherwise.
 */
const isGdprInitSessionActive = (): boolean => {
  // Map to isConsentBannerBeingShown - banner visible = user making decision
  if (isKetchMode()) {
    return isGdprInitSessionActiveViaKetch();
  }

  const gdprSingleCookie = getSingleValueCookie(GDPR_COOKIE_NAME);
  return (
    (gdprSingleCookie.getValue() || '').indexOf(
      `${GDPR_SESSION_COOKIE_FLAG}:::true`,
    ) >= 0
  );
};

const isInitialLoad = (): boolean => {
  const $window = GLOBAL.getWindow() as Window;

  const hasInitialLoadFlagOnWindow =
    $window.Sb &&
    $window.Sb['trackingPreferences'] &&
    // @ts-expect-error -- window based storage is not typed
    $window.Sb['trackingPreferences'].initialLoad;
  const hasInitialLoadValueInCookie = isGdprInitSessionActive();

  return hasInitialLoadFlagOnWindow || hasInitialLoadValueInCookie;
};

const setInitialLoadFlag = () => {
  const $window = GLOBAL.getWindow() as Window;
  if (!$window.Sb) {
    $window.Sb = {};
  }
  if (!$window.Sb['trackingPreferences']) {
    $window.Sb['trackingPreferences'] = {};
  }
  // @ts-expect-error -- window based storage is not typed
  $window.Sb['trackingPreferences'].initialLoad = true;
};

/**
 * Sets the preferences to the inital default preferences for the selected market.
 * The market is necessary because the default preferences may vary between the markets.
 * The function fires 'tracking-preferences-changed' event on completion
 * @param market a market (for example 'RU', 'UK', 'US', etc.)
 */
const setInitialPreferences = (market: string): void => {
  // No-op in Ketch mode - Ketch SDK handles initial state
  if (isKetchMode()) {
    return;
  }

  if (isLatestCookieVersion()) {
    throw new Error(
      'There is already a GDPR cookie with the latest version present',
    );
  }

  setInitialLoadFlag();
  setPreferencesToCookie(getDefaultInitialisationPreferences(market), true);
};

/**
 * If the cookie is not present or is in an older version,
 * this method will return a cookie containing the default preferences for the market.
 *
 * If the cookie is present and is in a supported version (>= LATEST_VERSION) and is a permanent cookie, the cookie's preferences will be returned.
 * If the cookie is present and is in a supported version and it is a SESSION cookie, it will be converted AND STORED (via convertSessionCookieToNormal method)
 * into a permanent cookie (meaning the `init` flag will be stripped away and an expiry date will be set) and then the cookie's preferences will be returned.
 * @param market a market (for example 'RU', 'UK', 'US', etc.)
 * @param marketDefaults Default values of the GDPR cookie preferences for the market
 * @returns the GDPR cookie preferences
 */
const validatedGdprCookie = (
  market: string,
  marketDefaults: MarketDefaults,
): TrackingPreferences => {
  if (!cookieExists(GDPR_COOKIE_NAME)) {
    return attachGdprActiveField(market, marketDefaults);
  }

  const gdprCookie = getMultiValueCookie(GDPR_COOKIE_NAME);
  const userVersion = getVersion(gdprCookie);
  // Use >= LATEST_VERSION to accept both v3 (existing users) and v4 (new users with purpose-scoped ID service enabled).
  // This ensures existing v3 users' stored consent is respected when the experiment is enabled.
  if (userVersion < LATEST_VERSION) {
    return attachGdprActiveField(market, marketDefaults);
  }

  convertSessionCookieToNormal(gdprCookie);
  return getPreferencesFromCookie(gdprCookie, market);
};
/**
 * @param market a market (for example 'RU', 'UK', 'US', etc.)
 * @returns tracking preferences object for regulated markets
 */
const getRegulatedMarketPreferences = (market: string): TrackingPreferences => {
  if (isInitialLoad()) {
    return {
      [PREFERENCES.INFORMATION]: REGULATED_MARKET_FALLBACK_OPTIN_VALUE,
      [PREFERENCES.ADVERTS]: REGULATED_MARKET_FALLBACK_OPTIN_VALUE,
      [PREFERENCES.VERSION]: getEffectiveLatestVersion(),
      [IS_GDPR_ACTIVE_FIELD_NAME]: isRegulatedMarket(market),
      [PREFERENCES.TC_STRING]: TC_STRING_DEFAULT_VALUE,
    };
  }

  return validatedGdprCookie(market, REGULATED_MARKET_DEFAULTS);
};
/**
 *
 * @param market a market (for example 'RU', 'UK', 'US', etc.)
 * @returns tracking preferences object for non-regulated markets
 */
const getNonRegulatedMarketPreferences = (
  market: string,
): TrackingPreferences =>
  validatedGdprCookie(market, NON_REGULATED_MARKET_DEFAULTS);

/**
 * Gets the tracking preferences for the selected market.
 * The function takes multiple context information into consideration before returning the value.
 * The function fires 'tracking-preferences-changed' event on completion
 * @param  market a market (for example 'RU', 'UK', 'US', etc.)
 * @returns  a tracking preferences object
 */
const getPreferences = (market: string): TrackingPreferences => {
  checkImportEnvironment();
  if (!market)
    throw new Error('Market should be provided to get tracking preferences');

  // Route to Ketch when enabled
  if (isKetchMode()) {
    return getPreferencesFromKetch(market);
  }

  // Downgrade cookie version if ahead of effective version (rollback scenario)
  maybeDowngradeCookieVersion();

  return isRegulatedMarket(market)
    ? getRegulatedMarketPreferences(market)
    : getNonRegulatedMarketPreferences(market);
};

/**
 * Checks if the cookie-banner should be shown.
 * This function should be kept in sync with the mirroring functionality in request-context/cookie-resolver (server-side part)
 * @param  market a market (for example 'RU', 'UK', 'US', etc.)
 * @returns  indicating whether to show or not the cookie-banner
 */
const showCookieBanner = (market: string): boolean => {
  if (!market)
    throw new Error(
      'Market should be provided to determine if cookie-banner is required to be shown',
    );

  // Route to Ketch when enabled
  if (isKetchMode()) {
    return showBannerViaKetch();
  }

  // Downgrade cookie version if ahead of effective version (rollback scenario)
  maybeDowngradeCookieVersion();

  if (isInitialLoad()) {
    return true;
  }
  if (isRegulatedMarket(market)) {
    return !isGdprInitialised();
  }
  return cookieExists(GDPR_COOKIE_NAME) && !isLatestCookieVersion();
};

/**
 * Removes the traveller from being in initial load state and fires the necessary events to update the subscribers.
 * The function will fire 'tracking-preferences-changed' event when initial state load flag is reset.
 * @throws in case the function is invoked while the GDPR cookie (tracking settings) is not set or is in an invalid format/version
 */
const acknowledgeTrackingSettings = (): void => {
  // No-op in Ketch mode - Ketch SDK handles acknowledgment
  if (isKetchMode()) {
    return;
  }

  if (isGdprInitialised()) {
    if (isInitialLoad()) {
      const gdprCookie = getMultiValueCookie(GDPR_COOKIE_NAME);
      resetInitialLoadFlag();
      storeGdprCookie(gdprCookie, false);
      firePreferencesChangedEvent();
    }
    return;
  }

  throw new Error(
    'Tracking settings are not complete. Either not set or invalid',
  );
};

export {
  getPreferences,
  setPreferences,
  setInitialPreferences,
  acknowledgeTrackingSettings,
  showCookieBanner,
  isGdprInitialised,
  isGdprInitSessionActive,
};
